import { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { toast } from 'react-toastify'
import { UserRole } from '../../types/ApiTypes'
import { userService } from '../services/UserService'

type Props = {
  open: boolean
  onClose: () => void
  onAdd?: () => void
}

const roles = [
  { label: 'Administrator', value: UserRole.ADMIN },
  { label: 'Super-Admin', value: UserRole.SUPERADMIN }
]
const inputStyle = { boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.02)' }

export default function AddMemberModal({ open, onClose, onAdd }: Props): React.JSX.Element | null {
  const ref = useRef<HTMLDivElement | null>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState<UserRole>(UserRole.ADMIN)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!open) return
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    window.addEventListener('mousedown', handler)
    return () => window.removeEventListener('mousedown', handler)
  }, [open, onClose])

  if (!open) return null

  async function handleAdd() {
    if (!name || !email || !phone) return
    
    setLoading(true)
    try {
      const response = await userService.addUser({
        fullName: name,
        emailAddress: email,
        phoneNumber: phone,
        role
      })
      
      if (response.status === 'success') {
        toast.success(response.message || 'Member added successfully')
        setName('')
        setEmail('')
        setPhone('')
        setRole(UserRole.ADMIN)
        onClose()
        onAdd?.()
      } else {
        toast.error(response.message || 'Failed to add member')
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add member')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div role="dialog" aria-label="Add member" className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(16,24,40,0.4)] p-6">
      <div ref={ref} className="w-[450px] bg-white rounded-2xl p-6 shadow-[0_10px_30px_rgba(16,24,40,0.12)]">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Add member</h3>
          <button onClick={onClose} className="text-[#1D1D1F]"><Icon icon="material-symbols:close" width={16} /></button>
        </div>

        <div className="mt-4">
          <label className="text-sm text-[#56565C]">Full name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="John Doe" className="font-normal w-full mt-1 p-2 rounded-md border border-[#EDEDEE] text-sm placeholder:text-[#CACACC]" style={inputStyle} />
        </div>

        <div className="mt-4">
          <label className="text-sm text-[#56565C]">Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="johndoe@example.com" className="font-normal w-full mt-1 p-2 rounded-md border border-[#EDEDEE] text-sm placeholder:text-[#CACACC]" style={inputStyle} />
        </div>

        <div className="mt-4">
          <label className="text-sm text-[#56565C]">Phone number</label>
          <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+234 800 000 0000" className="font-normal w-full mt-1 p-2 rounded-md border border-[#EDEDEE] text-sm placeholder:text-[#CACACC]" style={inputStyle} />
        </div>

        <div className="mt-4">
          <label className="text-sm text-[#56565C]">Role</label>
          <select value={role} onChange={e => setRole(e.target.value as UserRole)} className="w-full mt-2 p-2 rounded-md border border-[#EEF0F2] text-sm" style={inputStyle}>
            {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onClose} disabled={loading} className="px-6 py-2 rounded-full text-sm bg-[#F7F7F8] hover:bg-[#E3E3E5] transition cursor-pointer disabled:opacity-50">Cancel</button>
          <button onClick={handleAdd} disabled={!name || !email || !phone || loading} className="px-6 py-2 rounded-full bg-[#C02236] text-white text-sm hover:bg-[#9a1a2a] transition cursor-pointer disabled:bg-[#F4C3C8]">{loading ? 'Adding...' : 'Add Member'}</button>
        </div>
      </div>
    </div>
  )
}
